import { useCallback, useEffect, useState } from "react";
import { ReceiptText, Search, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { EmptyState, ErrorState, Loading, PageHeader, StatusPill } from "@/components/Bits";
import { Field, ghostBtnCls, inputCls, primaryBtnCls, Table, Td } from "@/components/Kit";
import { Modal } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { dmyTime, inr, titleize } from "@/lib/format";
import { PrintPreview, HospitalInvoiceDoc, PrescriptionDoc } from "@/components/PrintDoc";
import { PharmacyInvoiceDoc } from "@/components/PharmacyInvoice";

export default function CentralInvoices() {
  const { user, can } = useAuth();
  const [items, setItems] = useState(null);
  const [modules, setModules] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  // Filters sent to the server. Page size stays 25 (the established pattern). The single `date`
  // filter is kept for backward compatibility; `from_date`/`to_date` power the range picker.
  const [filters, setFilters] = useState({ q: "", module: "", status: "", date: "", from_date: "", to_date: "" });
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ status: "paid", amount: "", mode: "cash", reason: "" });
  const [adjusting, setAdjusting] = useState(false);
  // Print state. `receipt` holds the fetched, server-computed document for the chosen module.
  const [receipt, setReceipt] = useState(null);
  const [rxPrint, setRxPrint] = useState(null);
  const [printing, setPrinting] = useState(false);

  const allowed = can("hospital.billing.manage") || can("pharmacy.sales.view") || can("lab.orders.view");
  const adminRole = Boolean(user) && ["business_admin", "hospital_admin", "pharmacy_admin"].includes(user.role);

  // Reset to the first page whenever any filter changes (never jump to a stale page).
  const updateFilters = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };

  const load = useCallback(async () => {
    if (!allowed) {
      return;
    }
    setItems(null);
    try {
      const params = { ...filters, page, limit: 25 };
      const { data } = await api.get("/invoices", { params });
      setItems(data.items || []);
      setModules(data.modules || []);
      setTotal(data.total || 0);
    } catch (requestError) {
      setError(apiError(requestError));
    }
  }, [allowed, filters, page]);

  const submitAdjustment = async (event) => {
    event.preventDefault();
    if (!adjustFor || !adjustForm.reason.trim()) return;
    setAdjusting(true);
    try {
      await api.post(`/invoices/${adjustFor.source}/${adjustFor.invoice_id}/adjust`, {
        status: adjustForm.status,
        amount: adjustForm.status === "paid" ? Number(adjustForm.amount) : undefined,
        mode: adjustForm.mode,
        reason: adjustForm.reason.trim(),
      });
      toast.success("Invoice payment adjustment recorded and audited.");
      setAdjustFor(null);
      setAdjustForm({ status: "paid", amount: "", mode: "cash", reason: "" });
      load();
    } catch (requestError) {
      toast.error(apiError(requestError));
    } finally {
      setAdjusting(false);
    }
  };

  // Print the invoice/bill/receipt through the existing print renderer, per module. Reuses the
  // same server-computed documents the module pages print, so no editing powers are granted —
  // this is a read-only fetch gated by the module's view/reprint permission.
  const printInvoice = async (invoice) => {
    setPrinting(true);
    try {
      if (invoice.source === "hospital") {
        const { data } = await api.get(`/hospital/invoices/${invoice.invoice_id}`);
        setReceipt(data);
      } else if (invoice.source === "pharmacy") {
        const { data } = await api.get(`/pharmacy/sales/${invoice.invoice_id}`);
        setReceipt(data);
      } else {
        const { data } = await api.get(`/lab/bills/${invoice.invoice_id}`);
        setReceipt({ bill: data.bill });
      }
    } catch (requestError) {
      toast.error(apiError(requestError));
    } finally {
      setPrinting(false);
    }
  };

  // Print the prescription linked to a hospital bill, when one exists. Backend enforces
  // tenant scope + hospital.billing.manage and returns 404 when there is no prescription.
  const printPrescription = async (invoice) => {
    setPrinting(true);
    try {
      const { data } = await api.get(`/invoices/${invoice.source}/${invoice.invoice_id}/prescription`);
      setRxPrint(data);
    } catch (requestError) {
      toast.error(apiError(requestError));
    } finally {
      setPrinting(false);
    }
  };

  const canPrintInvoice = (invoice) => {
    if (invoice.source === "hospital") return can("hospital.billing.manage");
    if (invoice.source === "pharmacy") return can("pharmacy.sales.reprint");
    if (invoice.source === "laboratory" || invoice.source === "diagnostic") return can("lab.report.print");
    return false;
  };
  const canPrintPrescription = (invoice) =>
    invoice.source === "hospital" && can("hospital.billing.manage") && can("hospital.prescriptions.manage");

  useEffect(() => {
    load();
  }, [load]);

  if (!allowed) {
    return <NoAccess what="the central invoice repository" />;
  }

  const lastPage = Math.max(1, Math.ceil(total / 25));

  return (
    <div data-testid="central-invoices-page">
      <PageHeader
        overline="Finance · All modules"
        title="Central invoices"
        subtitle="Search invoice and receipt history across only the modules your role is permitted to view. Results are always scoped to this business."
      />

      <div className="mb-6 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_150px_150px_150px]">
        <label className="relative">
          <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <input className={`${inputCls} pl-9`} data-testid="central-invoice-search" onChange={(event) => updateFilters({ q: event.target.value })} placeholder="Invoice number, patient, UHID or customer…" value={filters.q} />
        </label>
        <select className={inputCls} data-testid="central-invoice-module-filter" onChange={(event) => updateFilters({ module: event.target.value })} value={filters.module}>
          <option value="">All modules</option>
          {modules.map((module) => <option key={module} value={module}>{titleize(module)}</option>)}
        </select>
        <select className={inputCls} data-testid="central-invoice-status-filter" onChange={(event) => updateFilters({ status: event.target.value })} value={filters.status}>
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
          <option value="unpaid">Unpaid</option>
        </select>
        <label className="relative">
          <span className="pointer-events-none absolute left-3 top-1.5 text-[10px] uppercase tracking-wider text-slate-400">From</span>
          <input className={`${inputCls} pt-6`} data-testid="central-invoice-from-filter" onChange={(event) => updateFilters({ from_date: event.target.value, date: "" })} type="date" value={filters.from_date} />
        </label>
        <label className="relative">
          <span className="pointer-events-none absolute left-3 top-1.5 text-[10px] uppercase tracking-wider text-slate-400">To</span>
          <input className={`${inputCls} pt-6`} data-testid="central-invoice-to-filter" onChange={(event) => updateFilters({ to_date: event.target.value, date: "" })} type="date" value={filters.to_date} />
        </label>
      </div>

      <Modal open={Boolean(adjustFor)} onClose={() => !adjusting && setAdjustFor(null)}
        title="Adjust invoice payment"
        subtitle={adjustFor ? `${adjustFor.invoice_no} · ${adjustFor.module}` : ""}
        testid="central-invoice-adjust-modal">
        {adjustFor && (
          <form onSubmit={submitAdjustment} className="space-y-4">
            <Field label="Correct payment status">
              <select className={inputCls} data-testid="central-invoice-adjust-status"
                value={adjustForm.status}
                onChange={(event) => setAdjustForm({ ...adjustForm, status: event.target.value })}>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid / reverse recorded collection</option>
              </select>
            </Field>
            {adjustForm.status === "paid" ? (
              <>
                <Field label="Amount to collect (₹) *">
                  <input required min="0.01" step="0.01" type="number" className={inputCls}
                    data-testid="central-invoice-adjust-amount" value={adjustForm.amount}
                    onChange={(event) => setAdjustForm({ ...adjustForm, amount: event.target.value })} />
                </Field>
                <Field label="Payment mode *">
                  <select className={inputCls} data-testid="central-invoice-adjust-mode" value={adjustForm.mode}
                    onChange={(event) => setAdjustForm({ ...adjustForm, mode: event.target.value })}>
                    {["cash", "upi", "card", "bank", "insurance", "cheque", "online", "other"].map((mode) => (
                      <option key={mode} value={mode}>{mode}</option>
                    ))}
                  </select>
                </Field>
              </>
            ) : (
              <p className="text-sm text-amber-800" data-testid="central-invoice-reversal-amount">
                A reversal for the recorded net collection of {inr(adjustFor.paid)} will be added. Historical payments will remain in the ledger.
              </p>
            )}
            <Field label="Reason *">
              <textarea required minLength={3} rows={3} className={inputCls}
                data-testid="central-invoice-adjust-reason" value={adjustForm.reason}
                onChange={(event) => setAdjustForm({ ...adjustForm, reason: event.target.value })} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" disabled={adjusting} onClick={() => setAdjustFor(null)} className={ghostBtnCls}>Cancel</button>
              <button disabled={adjusting} className={primaryBtnCls} data-testid="central-invoice-adjust-submit">
                {adjusting ? "Saving..." : "Record adjustment"}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {error && <ErrorState message={error} />}
      {!items ? <Loading label="Loading invoice history…" /> : (
        <>
        <Table
          columns={["Invoice", "Module", "Patient / customer", "Phone", "Reference", "Total", "Paid", "Due", "Payment mode", "Status", "Created by", "Actions"]}
          empty={<EmptyState title="No matching invoices" subtitle="Try a different module, date or search term." testid="central-invoices-empty" />}
          rows={items}
          testid="central-invoices-table"
          renderRow={(invoice) => (
            <tr data-testid={`central-invoice-row-${invoice.source}-${invoice.invoice_id}`} key={`${invoice.source}-${invoice.invoice_id}`}>
              <Td className="font-medium text-slate-900">
                <span className="inline-flex items-center gap-2"><ReceiptText className="h-3.5 w-3.5 text-slate-400" />{invoice.invoice_no}</span>
                <span className="block text-xs text-slate-400">{dmyTime(invoice.created_at)}</span>
              </Td>
              <Td className="capitalize text-slate-600">{invoice.module}</Td>
              <Td className="text-slate-800">{invoice.patient_name || "Walk-in customer"}<span className="block text-xs text-slate-400">{invoice.uhid || "—"}</span></Td>
              <Td className="text-slate-600">{invoice.phone || "—"}</Td>
              <Td className="text-slate-600">{invoice.reference || "—"}</Td>
              <Td className="text-slate-800">{inr(invoice.total)}</Td>
              <Td className="text-emerald-700">{inr(invoice.paid)}</Td>
              <Td className={invoice.outstanding > 0 ? "font-medium text-amber-700" : "text-slate-500"}>{inr(invoice.outstanding)}</Td>
              <Td className="text-slate-600">{invoice.payment_mode || "—"}</Td>
              <Td><StatusPill status={invoice.status} /></Td>
              <Td className="text-slate-600">{invoice.created_by_name || "—"}<span className="block text-xs text-slate-400">{invoice.created_by_shift || ""}</span></Td>
              <Td>
                <div className="flex flex-wrap gap-2">
                  {canPrintInvoice(invoice) && (
                    <button className={ghostBtnCls} data-testid={`central-invoice-print-${invoice.invoice_id}`}
                      disabled={printing}
                      onClick={() => printInvoice(invoice)}>Print</button>
                  )}
                  {canPrintPrescription(invoice) && (
                    <button className={ghostBtnCls} data-testid={`central-invoice-print-rx-${invoice.invoice_id}`}
                      disabled={printing}
                      onClick={() => printPrescription(invoice)}>
                      <Stethoscope className="mr-1 inline h-3.5 w-3.5" />Rx
                    </button>
                  )}
                  {adminRole && ((invoice.source === "hospital" && can("hospital.payments.manage"))
                    || (invoice.source === "pharmacy" && can("pharmacy.sales.return"))
                    || (invoice.source === "laboratory" && can("lab.billing.manage"))) && (
                    <button className={ghostBtnCls} data-testid={`central-invoice-adjust-${invoice.invoice_id}`}
                      onClick={() => {
                        setAdjustFor(invoice);
                        setAdjustForm({ status: "paid", amount: String(invoice.outstanding || ""), mode: "cash", reason: "" });
                      }}>Edit / Adjust</button>
                  )}
                </div>
              </Td>
            </tr>
          )}
        />
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500" data-testid="central-invoices-pagination">
          <span data-testid="central-invoices-count">
            {total === 0 ? "No invoices" : `Showing page ${page} of ${lastPage} · ${total} invoice(s)`}
          </span>
          <div className="flex gap-3">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className={ghostBtnCls} data-testid="central-invoices-prev-btn">Previous</button>
            <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)} className={ghostBtnCls} data-testid="central-invoices-next-btn">Next</button>
          </div>
        </div>
        </>
      )}

      <PrintPreview open={Boolean(receipt)} onClose={() => setReceipt(null)}
        title={receipt?.invoice ? `Invoice ${receipt.invoice.invoice_no || ""}` : `Bill ${receipt?.bill?.reg_no || ""}`}
        subtitle={receipt?.invoice?.patient_name || receipt?.patient?.name || receipt?.bill?.patient_name}
        testid="central-invoice-print-preview">
        {receipt?.invoice && (
          <HospitalInvoiceDoc invoice={receipt.invoice} patient={receipt.patient} business={receipt.business} />
        )}
        {receipt?.sale && (
          <PharmacyInvoiceDoc sale={receipt.sale} business={receipt.business} />
        )}
        {receipt?.bill && (
          <div className="text-sm">
            <p className="font-ui text-lg font-bold">{receipt.bill.patient_name}</p>
            <p className="text-slate-500">Bill {receipt.bill.reg_no} · {dmyTime(receipt.bill.created_at)}</p>
            <p className="mt-3">Total: {inr(receipt.bill.total)} · Paid: {inr(receipt.bill.paid)} · Due: {inr(receipt.bill.outstanding ?? Math.max((receipt.bill.total || 0) - (receipt.bill.paid || 0), 0))}</p>
          </div>
        )}
      </PrintPreview>

      <PrintPreview open={Boolean(rxPrint)} onClose={() => setRxPrint(null)}
        title="Prescription" subtitle={rxPrint?.patient?.name} prescriptionPreview
        testid="central-invoice-rx-print-preview">
        <PrescriptionDoc rx={rxPrint?.prescription} patient={rxPrint?.patient} business={rxPrint?.business} />
      </PrintPreview>
    </div>
  );
}